/**
 * Cliente das rotas de gateways WhatsApp por organização (spec 2026-09-23 §4/§5).
 * O axios de ./client já usa baseURL '/api': os caminhos começam em '/v1/...'.
 */
import { api } from './client'

export type EstadoGateway = 'provisionando' | 'provisionado' | 'atualizando' | 'falhou' | 'removendo' | 'removido'

export interface GatewayWhatsApp {
  id: string
  organization_id: number
  name: string
  status: EstadoGateway
  image_tag: string
  created_at: string
  updated_at: string
  removed_at: string | null
}

export interface PassoDoJob {
  passo: string
  status: 'running' | 'success' | 'failed'
  iniciado_em: string
  concluido_em: string | null
  tentativas: number
  ultimo_erro: string | null
}

export interface JobDoGateway {
  id?: number
  kind: 'provision' | 'deprovision' | 'update'
  status: 'pending' | 'running' | 'success' | 'failed'
  step: string | null
  last_error: string | null
  updated_at: string
  created_at?: string
  finished_at?: string | null
  /** Linha do tempo (MIGRATION_393). Ausente em job anterior a ela. */
  passos?: PassoDoJob[]
  /** Job falho que este retomou (`POST …/retry`), e o que a cadeia já tinha concluído. */
  retomado_de?: number | null
  passos_anteriores?: (PassoDoJob & { job_id: number })[]
}

/** Linha da aba "Operações": um job do gateway, sem os passos (esses vêm por `passosDoJob`). */
export interface OperacaoDoGateway {
  id: number
  kind: JobDoGateway['kind']
  status: JobDoGateway['status']
  step: string | null
  /** Código cru (`last_error`); a frase pt-BR sai de `passosDoJob.fraseDoErro`. */
  erro: string | null
  /** Nulo no job automático (reconciliação de versão), que não tem pessoa. */
  requested_by_nome: string | null
  created_at: string
  finished_at: string | null
  duracao_s: number | null
}

export interface PaginaDeOperacoes {
  items: OperacaoDoGateway[]
  /** Cursor da página seguinte (`antes_de`); nulo quando acabou. */
  proximo: number | null
}

export interface GatewayDetalhe extends GatewayWhatsApp {
  job: JobDoGateway | null
  /** A `IMAGE_TAG` do servidor: o selo "Desatualizado" compara com o mesmo valor do worker. */
  versao_corrente: string
}

export interface ListaDeGateways {
  items: GatewayWhatsApp[]
  versao_corrente: string
}

export interface OrgComAcesso {
  id: number
  name: string
  /** `manage_keys` já vem como "manage_keys OU manage" do backend (org_access.GERIR_CHAVES). */
  pode: { view: boolean; manage: boolean; pair: boolean; manage_keys: boolean }
}

export interface InicioPareamento {
  qr_data_url: string
  /** Nulo quando o gateway devolveu o QR de um login já ativo: o prazo real é menor. */
  expira_em: string | null
}

export type EstadoEspera = 'aguardando' | 'renovado' | 'pareado' | 'expirado' | 'sem_login'

export interface Espera {
  estado: EstadoEspera
  qr_data_url?: string
}

export interface ChaveDaOrg {
  id: string
  name: string
  key_prefix: string
  is_active: boolean
  gateway_id: string | null
  /** Gateway do env (EC2) de uma chave sem gateway_id; null quando não há. */
  gateway_legado: string | null
  last_used_at: string | null
}

/** Resposta de criar/rotacionar: a key em texto aparece só aqui, uma vez. */
export interface ChaveComSegredo {
  item: ChaveDaOrg
  key: string
}

export interface LinhaDaTrilha {
  action: string
  created_at: string
  ator: string
  outcome: string
}

export type EstadoDaParte = 'ok' | 'alerta' | 'erro' | 'desconhecido'
export type ParteDoGateway = 'whatsapi_registro' | 'servico_ecs' | 'versao' | 'sessao' | 'chaves' | 'bot'

/** `GET .../{gw_id}/saude`: o estado ATUAL, conferido parte por parte (cache de 30 s no servidor;
 * `atualizar` confere de novo, no máximo uma vez a cada 5 s por gateway). */
export interface SaudeDoGateway {
  verificado_em: string
  itens: { chave: ParteDoGateway; estado: EstadoDaParte; titulo: string; detalhe: string }[]
}

/** Conversa em atendimento humano (pausa ativa do bot). `numero` já vem mascarado; `ref` é opaco. */
export interface ConversaEmPausa {
  ref: string
  numero: string
  motivo: string
  desde: string
  pausado_ate: string
}

/** `disponivel: false` = WhatsApi anterior à rota das conversas; `tem_bot: false` = gateway sem bot. */
export interface ConversasEmPausa {
  disponivel: boolean
  tem_bot: boolean
  items: ConversaEmPausa[]
}

// A espera segura 25 s na bridge e 35 s no ConsoleAI; 40 s no navegador fica acima.
const TIMEOUT_ESPERA_MS = 40_000

const base = (orgId: number) => `/v1/organizations/${orgId}/whatsapp-gateways`
const doGw = (orgId: number, gwId: string) => `${base(orgId)}/${gwId}`

export const whatsappGatewaysApi = {
  organizacoes: () =>
    api.get<{ items: OrgComAcesso[] }>('/v1/whatsapp-gateways/organizacoes').then((r) => r.data.items),
  listar: (orgId: number) =>
    api.get<ListaDeGateways>(base(orgId)).then((r) => r.data),
  obter: (orgId: number, gwId: string) =>
    api.get<GatewayDetalhe>(doGw(orgId, gwId)).then((r) => r.data),
  criar: (orgId: number, name: string) =>
    api.post<GatewayWhatsApp>(base(orgId), { name }).then((r) => r.data),
  renomear: (orgId: number, gwId: string, name: string) =>
    api.patch<GatewayWhatsApp>(doGw(orgId, gwId), { name }).then((r) => r.data),
  remover: (orgId: number, gwId: string) =>
    api.delete<GatewayWhatsApp>(doGw(orgId, gwId)).then((r) => r.data),
  reiniciar: (orgId: number, gwId: string) =>
    api.post<{ id: string; task_definition: string }>(`${doGw(orgId, gwId)}/restart`).then((r) => r.data),
  atualizar: (orgId: number, gwId: string) =>
    api.post<{ id: string; status: EstadoGateway; versao: string }>(`${doGw(orgId, gwId)}/atualizar`).then((r) => r.data),
  tentarDeNovo: (orgId: number, gwId: string) =>
    api.post<{ id: string; status: EstadoGateway; job: { id: number; step: string; retomado_de: number } }>(
      `${doGw(orgId, gwId)}/retry`).then((r) => r.data),
  trilha: (orgId: number, gwId: string) =>
    api.get<{ items: LinhaDaTrilha[] }>(`${doGw(orgId, gwId)}/trilha`).then((r) => r.data.items),
  operacoes: (orgId: number, gwId: string, antesDe?: number | null) =>
    api.get<PaginaDeOperacoes>(`${doGw(orgId, gwId)}/jobs`, { params: antesDe != null ? { antes_de: antesDe } : {} })
      .then((r) => r.data),
  passosDoJob: (orgId: number, gwId: string, jobId: number) =>
    api.get<JobDoGateway>(`${doGw(orgId, gwId)}/jobs/${jobId}/passos`).then((r) => r.data),
  saude: (orgId: number, gwId: string, atualizar = false) =>
    api.get<SaudeDoGateway>(`${doGw(orgId, gwId)}/saude`, { params: atualizar ? { atualizar: 1 } : {} })
      .then((r) => r.data),
  conversasEmPausa: (orgId: number, gwId: string) =>
    api.get<ConversasEmPausa>(`${doGw(orgId, gwId)}/bot/conversas`).then((r) => r.data),
  iniciarPareamento: (orgId: number, gwId: string) =>
    api.post<InicioPareamento>(`${doGw(orgId, gwId)}/pairing`).then((r) => r.data),
  /** O QR vai no corpo: na URL ele cairia em log de acesso. */
  aguardarPareamento: (orgId: number, gwId: string, qrAtual: string, signal?: AbortSignal) =>
    api
      .post<Espera>(`${doGw(orgId, gwId)}/pairing/wait`, { qr_atual: qrAtual }, { signal, timeout: TIMEOUT_ESPERA_MS })
      .then((r) => r.data),
  desparear: (orgId: number, gwId: string) =>
    api.post<{ cleared: boolean }>(`${doGw(orgId, gwId)}/pairing/unlink`).then((r) => r.data),
  chaves: (orgId: number) =>
    api.get<{ items: ChaveDaOrg[] }>(`${base(orgId)}/chaves`).then((r) => r.data.items),
  apontarChave: (orgId: number, gwId: string, keyId: string) =>
    api.put<{ item: ChaveDaOrg }>(`${doGw(orgId, gwId)}/chaves/${keyId}`).then((r) => r.data.item),
  desapontarChave: (orgId: number, gwId: string, keyId: string) =>
    api.delete<{ item: ChaveDaOrg }>(`${doGw(orgId, gwId)}/chaves/${keyId}`).then((r) => r.data.item),
  // Ciclo de vida no WhatsApi do ECS (spec 2026-10-16). `key` vem em texto UMA vez:
  // fica só no resultado da mutação, nunca numa query (o cache de queries persiste).
  criarChave: (orgId: number, gwId: string, name: string) =>
    api.post<ChaveComSegredo>(`${doGw(orgId, gwId)}/chaves`, { name }).then((r) => r.data),
  rotacionarChave: (orgId: number, gwId: string, keyId: string) =>
    api.post<ChaveComSegredo>(`${doGw(orgId, gwId)}/chaves/${keyId}/rotacionar`).then((r) => r.data),
  ativarChave: (orgId: number, gwId: string, keyId: string) =>
    api.post<{ item: ChaveDaOrg }>(`${doGw(orgId, gwId)}/chaves/${keyId}/ativar`).then((r) => r.data.item),
  desativarChave: (orgId: number, gwId: string, keyId: string) =>
    api.post<{ item: ChaveDaOrg }>(`${doGw(orgId, gwId)}/chaves/${keyId}/desativar`).then((r) => r.data.item),
  excluirChave: (orgId: number, gwId: string, keyId: string) =>
    api.post<{ ok: boolean }>(`${doGw(orgId, gwId)}/chaves/${keyId}/excluir`).then((r) => r.data),
}
